import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../theme';
import { useIsMobile } from '../useMediaQuery';
import { usePro } from '../contexts/ProContext';
import { useWallet } from '../contexts/WalletContext';
import {
  HiOutlineViewGrid,
  HiOutlineShieldCheck,
  HiOutlineStar,
  HiOutlineGlobe,
  HiOutlineFire,
  HiOutlineBriefcase,
  HiOutlineChartBar,
  HiOutlineLink,
  HiOutlineSwitchHorizontal,
  HiOutlineCalculator,
  HiOutlineBeaker,
  HiOutlineSearch,
  HiOutlineRefresh,
  HiOutlineBell,
  HiOutlineDocumentReport,
  HiOutlineCurrencyDollar,
  HiOutlineLightningBolt,
} from 'react-icons/hi';

const MAIN_TABS = [
  { key: 'explorer',  label: 'All Pools',   icon: HiOutlineViewGrid,     iconColor: '#627EEA' },
  { key: 'memecoins', label: 'Degen Pools',  icon: HiOutlineFire,         iconColor: '#FF4500', highlight: true, highlightColor: '#FF4500' },
  { key: 'bestpicks', label: 'Top Picks',   icon: HiOutlineStar,         iconColor: '#F5AC37', highlight: true },
  { key: 'trending',  label: 'Market',      icon: HiOutlineGlobe,        iconColor: '#5470D4' },
  { key: 'stables',   label: 'Safe Yields', icon: HiOutlineShieldCheck,  iconColor: '#0D7A4E' },
  { key: 'portfolio', label: 'Portfolio',   icon: HiOutlineBriefcase,    iconColor: '#8247E5' },
  { key: 'swap',      label: 'Swap',        icon: HiOutlineRefresh,      iconColor: '#0D7A4E', highlight: false },
];

const ANALYTICS_TABS = [
  { key: 'heatmap',     label: 'Heatmap',     icon: HiOutlineChartBar, iconColor: '#E84142' },
  { key: 'correlation', label: 'Correlation', icon: HiOutlineLink,     iconColor: '#2B6DEF' },
];

const TOOLS_TABS = [
  { key: 'compare',  label: 'Compare',   icon: HiOutlineSwitchHorizontal, iconColor: '#1FC7D4' },
  { key: 'farmcalc', label: 'Farm Calc', icon: HiOutlineBeaker,           iconColor: '#32D583' },
  { key: 'ilcalc',   label: 'IL Calc',   icon: HiOutlineCalculator,       iconColor: '#F0B90B' },
  { key: 'wallet',   label: 'Analyzer',  icon: HiOutlineSearch,           iconColor: '#8247E5' },
];

const PRO_TABS = [
  { key: 'compareRates', label: 'DeFi vs TradFi', icon: HiOutlineCurrencyDollar, iconColor: '#8A6A00', pro: true },
  { key: 'proAlerts',    label: 'Pro Alerts',     icon: HiOutlineBell,           iconColor: '#8A6A00', pro: true },
  { key: 'proAnalyzer',  label: 'Pro Analyzer',   icon: HiOutlineLightningBolt,  iconColor: '#8A6A00', pro: true },
  { key: 'report',       label: 'Report',         icon: HiOutlineDocumentReport, iconColor: '#8A6A00', pro: true },
  { key: 'rebalancing',  label: 'Rebalancing',    icon: HiOutlineRefresh,        iconColor: '#8A6A00', pro: true },
];

function DropdownMenu({ label, icon, tabs, view, setView, colors, poolCounts, dark, isDegenView, pro }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const closeTimer = useRef(null);
  const activeTab = tabs.find(t => t.key === view);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => { document.removeEventListener("mousedown", handler); clearTimeout(closeTimer.current); };
  }, []);

  const handleEnter = () => { clearTimeout(closeTimer.current); setOpen(true); };
  const handleLeave = () => { closeTimer.current = setTimeout(() => setOpen(false), 150); };

  return (
    <div ref={ref} style={{ position: "relative" }} onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 11,
          color: pro
            ? (activeTab ? '#FFD700' : '#C49A20')
            : (activeTab ? colors.green : (isDegenView ? "rgba(255,255,255,0.6)" : colors.textSecondary)),
          background: pro
            ? (open || activeTab
              ? (dark ? 'rgba(138,106,0,0.18)' : 'rgba(138,106,0,0.08)')
              : `linear-gradient(90deg, ${dark ? 'rgba(138,106,0,0.08)' : 'rgba(138,106,0,0.04)'} 40%, ${dark ? 'rgba(224,204,112,0.15)' : 'rgba(224,204,112,0.10)'} 50%, ${dark ? 'rgba(138,106,0,0.08)' : 'rgba(138,106,0,0.04)'} 60%)`)
            : (open ? (isDegenView ? "rgba(255,255,255,0.08)" : (dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)")) : "transparent"),
          backgroundSize: pro ? '200% 100%' : 'auto',
          animation: pro && !activeTab ? 'proNavShimmer 3s ease-in-out infinite' : 'none',
          border: pro
            ? `1px solid ${dark ? 'rgba(224,204,112,0.35)' : 'rgba(224,204,112,0.4)'}`
            : (activeTab ? `1px solid ${dark ? 'rgba(46,160,67,0.3)' : 'rgba(13,122,78,0.2)'}` : `1px solid transparent`),
          borderRadius: 20,
          padding: "5px 12px",
          cursor: "pointer",
          fontWeight: pro ? 700 : (activeTab ? 600 : 500),
          letterSpacing: pro ? 0.5 : 0.3,
          transition: "all 0.2s ease",
          display: "flex",
          alignItems: "center",
          gap: 6,
          whiteSpace: "nowrap",
          boxShadow: pro ? `0 0 10px rgba(224,204,112,${dark ? 0.2 : 0.15})` : 'none',
        }}
        onMouseEnter={e => {
          if (pro) {
            e.currentTarget.style.background = dark ? 'rgba(138,106,0,0.25)' : 'rgba(138,106,0,0.12)';
            e.currentTarget.style.boxShadow = `0 0 16px rgba(224,204,112,${dark ? 0.35 : 0.25})`;
            e.currentTarget.style.borderColor = dark ? 'rgba(224,204,112,0.5)' : 'rgba(224,204,112,0.6)';
          } else if (!activeTab) {
            e.currentTarget.style.background = isDegenView ? "rgba(255,255,255,0.08)" : (dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)");
            e.currentTarget.style.color = isDegenView ? "rgba(255,255,255,0.85)" : colors.textPrimary;
          }
        }}
        onMouseLeave={e => {
          if (pro) {
            e.currentTarget.style.background = (open || activeTab)
              ? (dark ? 'rgba(138,106,0,0.18)' : 'rgba(138,106,0,0.08)')
              : 'transparent';
            e.currentTarget.style.boxShadow = `0 0 10px rgba(224,204,112,${dark ? 0.2 : 0.15})`;
            e.currentTarget.style.borderColor = dark ? 'rgba(224,204,112,0.35)' : 'rgba(224,204,112,0.4)';
          } else if (!activeTab && !open) {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.color = isDegenView ? "rgba(255,255,255,0.6)" : colors.textSecondary;
          }
        }}
      >
        {React.createElement(icon, { size: 13, style: { opacity: pro ? 1 : 0.8, color: pro ? '#C49A20' : (tabs[0]?.iconColor || 'inherit') } })}
        {activeTab ? activeTab.label : label}
        <span style={{
          fontSize: 7,
          opacity: 0.5,
          transition: "transform 0.2s ease",
          transform: open ? "rotate(180deg)" : "rotate(0)",
          marginLeft: -2,
        }}>&#9660;</span>
      </button>

      {open && (
        <div style={{
          position: "absolute",
          top: "calc(100% + 8px)",
          left: "50%",
          transform: "translateX(-50%)",
          background: dark ? colors.surface : "#FFFFFF",
          border: `1px solid ${colors.border}`,
          borderRadius: 12,
          padding: 6,
          boxShadow: dark
            ? "0 12px 32px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.05)"
            : "0 12px 32px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.04)",
          zIndex: 100,
          minWidth: 170,
          display: "flex",
          flexDirection: "column",
          gap: 2,
          animation: "dropdownIn 0.15s ease-out",
        }}>
          {/* Tiny arrow */}
          <div style={{
            position: "absolute",
            top: -5,
            left: "50%",
            transform: "translateX(-50%) rotate(45deg)",
            width: 10,
            height: 10,
            background: dark ? colors.surface : "#FFFFFF",
            borderTop: `1px solid ${colors.border}`,
            borderLeft: `1px solid ${colors.border}`,
          }} />
          {tabs.map(tab => {
            const active = view === tab.key;
            const count = poolCounts[tab.key];
            const goldActive = pro && active;
            return (
              <button
                key={tab.key}
                onClick={() => { setView(tab.key); setOpen(false); }}
                style={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 11,
                  color: goldActive ? '#C49A20' : (active ? colors.green : colors.textSecondary),
                  background: goldActive
                    ? (dark ? "rgba(138,106,0,0.15)" : "rgba(138,106,0,0.08)")
                    : (active ? (dark ? "rgba(46,160,67,0.12)" : "rgba(13,122,78,0.08)") : "transparent"),
                  border: "none",
                  borderRadius: 8,
                  padding: "8px 12px",
                  cursor: "pointer",
                  fontWeight: active ? 600 : 500,
                  letterSpacing: 0.3,
                  transition: "all 0.12s ease",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  whiteSpace: "nowrap",
                  textAlign: "left",
                  width: "100%",
                  position: "relative",
                }}
                onMouseEnter={e => {
                  if (!active) {
                    e.currentTarget.style.background = dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)";
                    e.currentTarget.style.color = colors.textPrimary;
                  }
                }}
                onMouseLeave={e => {
                  if (!active) {
                    e.currentTarget.style.background = "transparent";
                    e.currentTarget.style.color = colors.textSecondary;
                  }
                }}
              >
                <tab.icon size={13} style={{ opacity: active ? 1 : 0.7, color: (pro && active) ? '#C49A20' : (tab.iconColor || 'inherit') }} />
                {tab.label}
                {active && (
                  <span style={{
                    width: 5, height: 5, borderRadius: "50%",
                    background: pro ? '#C49A20' : colors.green, marginLeft: "auto",
                    ...(pro ? { boxShadow: '0 0 6px rgba(196,154,32,0.5)' } : {}),
                  }} />
                )}
                {count !== undefined && count > 0 && !active && (
                  <span style={{
                    fontFamily: "'IBM Plex Mono', monospace", fontSize: 9, fontWeight: 500,
                    color: colors.labelLight,
                    background: colors.badgeBg,
                    borderRadius: 8, padding: "1px 5px", lineHeight: "14px", marginLeft: "auto",
                  }}>{count}</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function Navbar({ view, setView, poolCounts = {}, lastUpdated, onRefresh }) {
  const { dark, colors, toggle } = useTheme();
  const isMobile = useIsMobile();
  const { isPro } = usePro();
  const { account, isConnected, isConnecting, connect, disconnect } = useWallet();
  const [elapsed, setElapsed] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [flashDot, setFlashDot] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const isDegenView = view === 'memecoins';

  useEffect(() => {
    if (!lastUpdated) return;
    setElapsed(0);
    setFlashDot(true);
    const flashTimer = setTimeout(() => setFlashDot(false), 1200);
    const interval = setInterval(() => {
      setElapsed(prev => prev + 1);
    }, 60000);
    return () => { clearInterval(interval); clearTimeout(flashTimer); };
  }, [lastUpdated]);

  const handleRefresh = () => {
    if (refreshing || !onRefresh) return;
    setRefreshing(true);
    onRefresh();
    setTimeout(() => setRefreshing(false), 1500);
  };

  const elapsedText = elapsed === 0 ? 'just now' : `${elapsed}m ago`;

  const renderTab = (tab) => {
    const active = view === tab.key;
    const count = poolCounts[tab.key];
    const isDegen = tab.key === 'memecoins';
    const isHighlight = tab.highlight && !active && !isDegen;
    const hc = tab.highlightColor || '#F5AC37';
    const hcR = parseInt(hc.slice(1,3),16), hcG = parseInt(hc.slice(3,5),16), hcB = parseInt(hc.slice(5,7),16);
    const hcRgba = (a) => `rgba(${hcR},${hcG},${hcB},${a})`;

    // Degen Pools gets a special treatment
    if (isDegen) {
      return (
        <button
          key={tab.key}
          onClick={() => { setView(tab.key); if (isMobile) setMenuOpen(false); }}
          style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: isMobile ? 12 : 11,
            fontWeight: 700,
            letterSpacing: 0.5,
            color: active ? '#FFFFFF' : '#FF4500',
            background: active
              ? 'linear-gradient(135deg, #FF4500 0%, #E8471A 50%, #D4380D 100%)'
              : dark
                ? 'linear-gradient(135deg, rgba(255,69,0,0.15) 0%, rgba(232,71,26,0.08) 100%)'
                : 'linear-gradient(135deg, rgba(255,69,0,0.1) 0%, rgba(232,71,26,0.05) 100%)',
            border: active
              ? '1px solid rgba(255,69,0,0.6)'
              : `1px solid ${dark ? 'rgba(255,69,0,0.35)' : 'rgba(255,69,0,0.25)'}`,
            borderRadius: 20,
            padding: active
              ? (isMobile ? "8px 16px" : "5px 14px")
              : (isMobile ? "8px 14px" : "5px 12px"),
            cursor: "pointer",
            transition: "all 0.2s ease",
            display: "flex",
            alignItems: "center",
            gap: 5,
            whiteSpace: "nowrap",
            boxShadow: active
              ? '0 0 16px rgba(255,69,0,0.4), 0 0 4px rgba(255,69,0,0.2)'
              : `0 0 10px rgba(255,69,0,${dark ? 0.2 : 0.12})`,
            position: 'relative',
            overflow: 'hidden',
            ...(isMobile ? { width: '100%', justifyContent: 'flex-start' } : {}),
          }}
          onMouseEnter={e => {
            if (!active && !isMobile) {
              e.currentTarget.style.background = dark
                ? 'linear-gradient(135deg, rgba(255,69,0,0.25) 0%, rgba(232,71,26,0.15) 100%)'
                : 'linear-gradient(135deg, rgba(255,69,0,0.18) 0%, rgba(232,71,26,0.1) 100%)';
              e.currentTarget.style.boxShadow = '0 0 14px rgba(255,69,0,0.3)';
              e.currentTarget.style.borderColor = 'rgba(255,69,0,0.5)';
            }
          }}
          onMouseLeave={e => {
            if (!active && !isMobile) {
              e.currentTarget.style.background = dark
                ? 'linear-gradient(135deg, rgba(255,69,0,0.15) 0%, rgba(232,71,26,0.08) 100%)'
                : 'linear-gradient(135deg, rgba(255,69,0,0.1) 0%, rgba(232,71,26,0.05) 100%)';
              e.currentTarget.style.boxShadow = `0 0 10px rgba(255,69,0,${dark ? 0.2 : 0.12})`;
              e.currentTarget.style.borderColor = dark ? 'rgba(255,69,0,0.35)' : 'rgba(255,69,0,0.25)';
            }
          }}
        >
          <span style={{ fontSize: isMobile ? 13 : 11 }}>{"\uD83D\uDD25"}</span>
          {tab.label}
          {count !== undefined && count > 0 && (
            <span style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 9,
              fontWeight: 600,
              color: active ? 'rgba(255,255,255,0.85)' : '#FF4500',
              background: active ? 'rgba(255,255,255,0.2)' : 'rgba(255,69,0,0.15)',
              borderRadius: 8,
              padding: "1px 5px",
              lineHeight: "14px",
            }}>{count}</span>
          )}
        </button>
      );
    }

    const isProTab = tab.pro;

    return (
      <button
        key={tab.key}
        onClick={() => { setView(tab.key); if (isMobile) setMenuOpen(false); }}
        style={{
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: isMobile ? 12 : 11,
          color: isProTab
            ? (active ? '#FFFFFF' : '#C49A20')
            : (active ? "#FFFFFF" : isHighlight ? hc : (isDegenView ? "rgba(255,255,255,0.6)" : colors.textSecondary)),
          background: isProTab
            ? (active
              ? 'linear-gradient(135deg, #8A6A00 0%, #B8901A 100%)'
              : (dark ? 'rgba(138,106,0,0.08)' : 'rgba(138,106,0,0.04)'))
            : (active ? colors.green : isHighlight
              ? `linear-gradient(90deg, ${hcRgba(dark ? 0.08 : 0.06)} 40%, ${hcRgba(dark ? 0.18 : 0.14)} 50%, ${hcRgba(dark ? 0.08 : 0.06)} 60%)`
              : "transparent"),
          backgroundSize: isHighlight ? '200% 100%' : 'auto',
          animation: isHighlight ? 'shimmer 3s ease-in-out infinite' : 'none',
          border: isProTab
            ? `1px solid ${active ? 'rgba(224,204,112,0.5)' : (dark ? 'rgba(224,204,112,0.2)' : 'rgba(224,204,112,0.25)')}`
            : (isHighlight ? `1px solid ${hcRgba(dark ? 0.25 : 0.2)}` : "none"),
          borderRadius: 20,
          padding: active
            ? (isMobile ? "8px 16px" : "5px 14px")
            : (isMobile ? "8px 14px" : "5px 12px"),
          cursor: "pointer",
          fontWeight: isProTab ? 600 : (active ? 600 : isHighlight ? 600 : 500),
          letterSpacing: isProTab ? 0.4 : 0.3,
          transition: "all 0.2s ease",
          display: "flex",
          alignItems: "center",
          gap: 6,
          whiteSpace: "nowrap",
          ...(isProTab && active ? { boxShadow: '0 0 12px rgba(224,204,112,0.4)' } : {}),
          ...(isHighlight && !isMobile ? { boxShadow: `0 0 8px ${hcRgba(0.15)}` } : {}),
          ...(isMobile ? { width: '100%', justifyContent: 'flex-start' } : {}),
        }}
        onMouseEnter={e => {
          if (!active && !isMobile) {
            if (isProTab) {
              e.currentTarget.style.background = dark ? 'rgba(138,106,0,0.18)' : 'rgba(138,106,0,0.10)';
              e.currentTarget.style.borderColor = dark ? 'rgba(224,204,112,0.35)' : 'rgba(224,204,112,0.4)';
            } else if (isHighlight) {
              e.currentTarget.style.background = hcRgba(dark ? 0.15 : 0.12);
              e.currentTarget.style.boxShadow = `0 0 12px ${hcRgba(0.25)}`;
            } else {
              e.currentTarget.style.background = isDegenView ? "rgba(255,255,255,0.08)" : (dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)");
              e.currentTarget.style.color = isDegenView ? "rgba(255,255,255,0.85)" : colors.textPrimary;
            }
          }
        }}
        onMouseLeave={e => {
          if (!active && !isMobile) {
            if (isProTab) {
              e.currentTarget.style.background = dark ? 'rgba(138,106,0,0.08)' : 'rgba(138,106,0,0.04)';
              e.currentTarget.style.borderColor = dark ? 'rgba(224,204,112,0.2)' : 'rgba(224,204,112,0.25)';
            } else if (isHighlight) {
              e.currentTarget.style.background = hcRgba(dark ? 0.08 : 0.06);
              e.currentTarget.style.boxShadow = `0 0 8px ${hcRgba(0.15)}`;
            } else {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = isDegenView ? "rgba(255,255,255,0.6)" : colors.textSecondary;
            }
          }
        }}
      >
        <tab.icon size={isMobile ? 14 : 12} style={{
          color: isProTab
            ? (active ? 'rgba(255,255,255,0.9)' : '#C49A20')
            : (active ? "rgba(255,255,255,0.85)" : (tab.iconColor || colors.labelMuted)),
        }} />
        {tab.label}
        {count !== undefined && count > 0 && (
          <span style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 9,
            fontWeight: 500,
            color: active ? "rgba(255,255,255,0.7)" : colors.labelLight,
            background: active ? "rgba(255,255,255,0.15)" : colors.badgeBg,
            borderRadius: 8,
            padding: "1px 5px",
            lineHeight: "14px",
          }}>{count}</span>
        )}
      </button>
    );
  };

  const renderMobileMenu = () => {
    const sections = [
      { title: null, tabs: MAIN_TABS },
      { title: "Analytics", tabs: ANALYTICS_TABS },
      { title: "Tools", tabs: TOOLS_TABS },
      { title: "Pro \u{1F451}", tabs: PRO_TABS },
    ];
    return sections.map((section, i) => (
      <React.Fragment key={i}>
        {section.title && (
          <div style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 9,
            fontWeight: 600,
            color: section.title.includes('Pro') ? '#C49A20' : colors.labelMuted,
            letterSpacing: 1.2,
            textTransform: "uppercase",
            padding: "12px 8px 6px",
            borderTop: section.title.includes('Pro')
              ? `1px solid ${dark ? 'rgba(224,204,112,0.2)' : 'rgba(224,204,112,0.3)'}`
              : `1px solid ${colors.border}`,
            marginTop: 6,
            ...(section.title.includes('Pro') ? { textShadow: '0 0 6px rgba(224,204,112,0.3)' } : {}),
          }}>
            {section.title}
          </div>
        )}
        {section.tabs.map(renderTab)}
      </React.Fragment>
    ));
  };

  return (
    <>
      {/* Dropdown animation */}
      <style>{`
        @keyframes dropdownIn {
          from { opacity: 0; transform: translateX(-50%) translateY(-4px); }
          to   { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
        @keyframes shimmer {
          0%   { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        @keyframes proNavShimmer {
          0%   { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        @keyframes proGlow {
          0%, 100% { box-shadow: 0 0 4px rgba(224,204,112,0.3); }
          50%      { box-shadow: 0 0 10px rgba(224,204,112,0.6); }
        }
      `}</style>

      <nav style={{
        height: isMobile ? 50 : 56,
        background: isDegenView ? "#0D0600" : (dark ? colors.surface : "#FFFFFF"),
        borderBottom: isDegenView ? "1px solid rgba(255,69,0,0.2)" : `1px solid ${colors.border}`,
        boxShadow: isDegenView
          ? "0 1px 8px rgba(255,69,0,0.15)"
          : (dark ? "0 1px 3px rgba(0,0,0,0.3)" : "0 1px 3px rgba(0,0,0,0.04)"),
        position: "sticky",
        top: 0,
        zIndex: 50,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: isMobile ? "0 12px" : "0 24px",
        transition: "background-color 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease",
      }}>
        {/* Left: Logo + Tabs */}
        <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 8 : 10 }}>
          {isMobile && (
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              style={{
                background: "none",
                border: "none",
                fontSize: 18,
                color: isDegenView ? "#FFFFFF" : colors.textPrimary,
                cursor: "pointer",
                padding: "10px",
                minWidth: 44,
                minHeight: 44,
                lineHeight: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {menuOpen ? '\u2715' : '\u2630'}
            </button>
          )}

          {/* Logo - Animated radar */}
          <div
            onClick={() => { setView('explorer'); if (isMobile) setMenuOpen(false); }}
            style={{
            cursor: "pointer",
            width: isMobile ? 38 : 46,
            height: isMobile ? 38 : 46,
            borderRadius: 11,
            background: `linear-gradient(to bottom, #0D7A4E, #0A5E3A)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            WebkitMaskImage: "radial-gradient(white, white)",
            outline: "none",
            border: "none",
            boxShadow: "none",
          }}>
            <style>{`
              @keyframes radarSweep { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
              @keyframes blip { 0%,100% { opacity: 0.15; } 50% { opacity: 0.9; } }
            `}</style>
            <svg viewBox="0 0 512 512" width={isMobile ? 32 : 40} height={isMobile ? 32 : 40} style={{ background: 'transparent', display: 'block' }}>
              {/* Grid rings */}
              <circle cx="256" cy="256" r="170" fill="none" stroke="white" strokeWidth="6" opacity="0.16"/>
              <circle cx="256" cy="256" r="125" fill="none" stroke="white" strokeWidth="5" opacity="0.19"/>
              <circle cx="256" cy="256" r="80" fill="none" stroke="white" strokeWidth="5" opacity="0.22"/>
              {/* Cross lines */}
              <line x1="86" y1="256" x2="426" y2="256" stroke="white" strokeWidth="4" opacity="0.12"/>
              <line x1="256" y1="86" x2="256" y2="426" stroke="white" strokeWidth="4" opacity="0.12"/>
              {/* Outer ring */}
              <circle cx="256" cy="256" r="170" fill="none" stroke="white" strokeWidth="24" opacity="0.8"/>
              {/* Animated sweep */}
              <g style={{ transformOrigin: "256px 256px", animation: "radarSweep 3s linear infinite" }}>
                <path d="M256 256 L256 86 A170 170 0 0 1 376 136 Z" fill="white" opacity="0.15"/>
                <line x1="256" y1="256" x2="256" y2="86" stroke="white" strokeWidth="6" opacity="0.9"/>
              </g>
              {/* Pool dots with staggered blink */}
              <circle cx="310" cy="180" r="12" fill="white" style={{ animation: "blip 3s ease-in-out 0s infinite" }}/>
              <circle cx="190" cy="195" r="10" fill="white" style={{ animation: "blip 3s ease-in-out 0.8s infinite" }}/>
              <circle cx="345" cy="305" r="11" fill="white" style={{ animation: "blip 3s ease-in-out 1.5s infinite" }}/>
              <circle cx="195" cy="330" r="9" fill="white" style={{ animation: "blip 3s ease-in-out 2s infinite" }}/>
              <circle cx="285" cy="355" r="8" fill="white" style={{ animation: "blip 3s ease-in-out 2.5s infinite" }}/>
              {/* Center */}
              <circle cx="256" cy="256" r="10" fill="white" opacity="0.95"/>
            </svg>
          </div>
          <span
            onClick={() => { setView('explorer'); if (isMobile) setMenuOpen(false); }}
            style={{
            fontFamily: "'DM Serif Display', serif",
            fontSize: isMobile ? 16 : 19,
            letterSpacing: -0.3,
            cursor: "pointer",
          }}>
            <span style={{ color: isDegenView ? "#FFFFFF" : colors.textPrimary, transition: "color 0.3s ease" }}>Pool</span>
            <span style={{ color: colors.green }}>Scope</span>
          </span>
          {!isMobile && (
            <>
              <span style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 8,
                color: isDegenView ? "rgba(255,255,255,0.5)" : colors.labelMuted,
                background: isDegenView ? "rgba(255,255,255,0.08)" : (dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)"),
                border: `1px solid ${isDegenView ? "rgba(255,69,0,0.2)" : colors.border}`,
                padding: "2px 6px",
                borderRadius: 4,
                letterSpacing: 0.8,
                fontWeight: 600,
              }}>
                BETA
              </span>
              {isPro && (
                <span style={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 8,
                  color: '#FFD700',
                  background: dark
                    ? 'linear-gradient(135deg, rgba(138,106,0,0.25), rgba(196,154,32,0.2))'
                    : 'linear-gradient(135deg, #FFF8E0, #FFF0C0)',
                  border: `1px solid ${dark ? 'rgba(224,204,112,0.4)' : '#E0CC70'}`,
                  padding: "2px 8px",
                  borderRadius: 4,
                  letterSpacing: 0.8,
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 3,
                  animation: 'proGlow 2.5s ease-in-out infinite',
                  textShadow: '0 0 4px rgba(224,204,112,0.4)',
                }}>
                  {'\u{1F451}'} PRO
                </span>
              )}
            </>
          )}

          {/* Desktop: tabs + dropdowns */}
          {!isMobile && (
            <>
              <div style={{
                width: 1,
                height: 22,
                background: isDegenView ? "rgba(255,255,255,0.12)" : colors.border,
                margin: "0 10px 0 6px",
              }} />
              <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
                {MAIN_TABS.map(renderTab)}
                <div style={{
                  width: 1,
                  height: 16,
                  background: isDegenView ? "rgba(255,255,255,0.12)" : colors.border,
                  margin: "0 6px",
                  opacity: 0.6,
                }} />
                <DropdownMenu
                  label="Analytics"
                  icon={HiOutlineChartBar}
                  tabs={ANALYTICS_TABS}
                  view={view}
                  setView={setView}
                  colors={colors}
                  poolCounts={poolCounts}
                  dark={dark}
                  isDegenView={isDegenView}
                />
                <DropdownMenu
                  label="Tools"
                  icon={HiOutlineBeaker}
                  tabs={TOOLS_TABS}
                  view={view}
                  setView={setView}
                  colors={colors}
                  poolCounts={poolCounts}
                  dark={dark}
                  isDegenView={isDegenView}
                />
                <div style={{
                  width: 1,
                  height: 16,
                  background: isDegenView ? "rgba(255,255,255,0.12)" : colors.border,
                  margin: "0 4px",
                  opacity: 0.6,
                }} />
                <DropdownMenu
                  label={isPro ? "Pro" : "Pro \u2728"}
                  icon={HiOutlineLightningBolt}
                  tabs={PRO_TABS}
                  view={view}
                  setView={setView}
                  colors={colors}
                  poolCounts={poolCounts}
                  dark={dark}
                  isDegenView={isDegenView}
                  pro
                />
              </div>
            </>
          )}
        </div>

        {/* Right: Controls */}
        <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 6 : 8 }}>
          {/* Wallet connect */}
          {isConnected ? (
            <button
              onClick={disconnect}
              style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: isMobile ? 10 : 11,
                fontWeight: 600,
                color: isDegenView ? "#FFFFFF" : colors.green,
                background: isDegenView ? "rgba(46,160,67,0.15)" : (dark ? "rgba(46,160,67,0.1)" : "rgba(13,122,78,0.06)"),
                border: `1px solid ${isDegenView ? 'rgba(46,160,67,0.3)' : (dark ? 'rgba(46,160,67,0.25)' : 'rgba(13,122,78,0.2)')}`,
                borderRadius: 20,
                padding: isMobile ? "6px 10px" : "5px 12px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6,
                whiteSpace: "nowrap",
                transition: "all 0.15s ease",
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = dark ? "rgba(184,0,26,0.12)" : "rgba(184,0,26,0.06)";
                e.currentTarget.style.borderColor = "rgba(184,0,26,0.3)";
                e.currentTarget.style.color = "#B8001A";
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = isDegenView ? "rgba(46,160,67,0.15)" : (dark ? "rgba(46,160,67,0.1)" : "rgba(13,122,78,0.06)");
                e.currentTarget.style.borderColor = isDegenView ? 'rgba(46,160,67,0.3)' : (dark ? 'rgba(46,160,67,0.25)' : 'rgba(13,122,78,0.2)');
                e.currentTarget.style.color = isDegenView ? "#FFFFFF" : colors.green;
              }}
            >
              <span style={{
                width: 6, height: 6, borderRadius: "50%",
                background: colors.green,
                boxShadow: `0 0 4px ${colors.green}`,
                flexShrink: 0,
              }} />
              {account?.slice(0, 6)}...{account?.slice(-4)}
            </button>
          ) : (
            <button
              onClick={connect}
              disabled={isConnecting}
              style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: isMobile ? 10 : 11,
                fontWeight: 600,
                color: isDegenView ? "#FFFFFF" : (dark ? "#FFFFFF" : colors.textPrimary),
                background: isDegenView ? "rgba(255,255,255,0.1)" : (dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.04)"),
                border: `1px solid ${isDegenView ? "rgba(255,255,255,0.2)" : (dark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.1)")}`,
                borderRadius: 20,
                padding: isMobile ? "6px 10px" : "5px 12px",
                cursor: isConnecting ? "default" : "pointer",
                opacity: isConnecting ? 0.6 : 1,
                display: "flex",
                alignItems: "center",
                gap: 6,
                whiteSpace: "nowrap",
                transition: "all 0.15s ease",
              }}
              onMouseEnter={e => {
                if (!isConnecting) {
                  e.currentTarget.style.background = isDegenView ? "rgba(255,255,255,0.15)" : (dark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.08)");
                  e.currentTarget.style.borderColor = isDegenView ? "rgba(255,255,255,0.35)" : colors.green;
                  e.currentTarget.style.color = colors.green;
                }
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = isDegenView ? "rgba(255,255,255,0.1)" : (dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.04)");
                e.currentTarget.style.borderColor = isDegenView ? "rgba(255,255,255,0.2)" : (dark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.1)");
                e.currentTarget.style.color = isDegenView ? "#FFFFFF" : (dark ? "#FFFFFF" : colors.textPrimary);
              }}
            >
              {isConnecting ? 'Connecting...' : '\uD83D\uDC5B Connect'}
            </button>
          )}

          {/* Dark mode */}
          <button
            onClick={toggle}
            style={{
              fontSize: 15,
              background: "transparent",
              border: `1px solid ${isDegenView ? "rgba(255,255,255,0.15)" : (dark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)")}`,
              borderRadius: "50%",
              width: isMobile ? 40 : 32,
              height: isMobile ? 40 : 32,
              cursor: "pointer",
              color: isDegenView ? "rgba(255,255,255,0.7)" : colors.textSecondary,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.2s ease",
              padding: 0,
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = isDegenView ? "rgba(255,255,255,0.1)" : (dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.04)");
              e.currentTarget.style.borderColor = isDegenView ? "rgba(255,255,255,0.3)" : colors.textSecondary;
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.borderColor = isDegenView ? "rgba(255,255,255,0.15)" : (dark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)");
            }}
          >
            {dark ? '\u2600' : '\u263E'}
          </button>

          {/* LIVE badge */}
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: 5,
            background: isDegenView ? "rgba(46,160,67,0.12)" : (dark ? "rgba(46,160,67,0.1)" : "rgba(13,122,78,0.06)"),
            border: `1px solid ${isDegenView ? 'rgba(46,160,67,0.25)' : (dark ? 'rgba(46,160,67,0.2)' : 'rgba(13,122,78,0.15)')}`,
            borderRadius: 20,
            padding: isMobile ? "4px 8px" : "4px 10px 4px 8px",
          }}>
            <div style={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: flashDot ? '#FFD700' : colors.green,
              animation: flashDot ? "pulse 0.3s ease-in-out 3" : "pulse 2s infinite",
              boxShadow: flashDot
                ? '0 0 8px rgba(255,215,0,0.6)'
                : `0 0 6px ${dark ? 'rgba(46,160,67,0.4)' : 'rgba(13,122,78,0.3)'}`,
              transition: "background 0.3s ease",
            }} />
            <span style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 9,
              color: colors.green,
              letterSpacing: 1,
              fontWeight: 700,
            }}>
              LIVE
            </span>
          </div>

          {/* Elapsed + Refresh (desktop only) */}
          {!isMobile && (
            <>
              <span style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 9,
                color: isDegenView ? "rgba(255,255,255,0.5)" : colors.labelLight,
                letterSpacing: 0.3,
              }}>
                {elapsedText}
              </span>
              <button
                onClick={handleRefresh}
                title="Refresh data"
                style={{
                  fontSize: 14,
                  background: "transparent",
                  border: "none",
                  cursor: refreshing ? "default" : "pointer",
                  color: refreshing ? colors.green : (isDegenView ? "rgba(255,255,255,0.5)" : colors.labelLight),
                  padding: "2px 4px",
                  lineHeight: 1,
                  transition: "color 0.2s ease, transform 0.6s ease",
                  transform: refreshing ? "rotate(360deg)" : "rotate(0deg)",
                  opacity: refreshing ? 1 : 0.6,
                }}
                onMouseEnter={e => {
                  if (!refreshing) { e.currentTarget.style.opacity = '1'; e.currentTarget.style.color = colors.green; }
                }}
                onMouseLeave={e => {
                  if (!refreshing) { e.currentTarget.style.opacity = '0.6'; e.currentTarget.style.color = isDegenView ? "rgba(255,255,255,0.5)" : colors.labelLight; }
                }}
              >
                {'\u21BB'}
              </button>
            </>
          )}
        </div>
      </nav>

      {/* Mobile menu */}
      {isMobile && menuOpen && (
        <div style={{
          position: "fixed",
          top: 50,
          left: 0,
          right: 0,
          bottom: 0,
          background: dark ? colors.surface : "#FFFFFF",
          zIndex: 49,
          padding: "8px 16px",
          display: "flex",
          flexDirection: "column",
          gap: 2,
          overflowY: "auto",
          animation: "dropdownIn 0.15s ease-out",
        }}>
          {/* Wallet button in mobile menu */}
          <div style={{
            padding: "12px 8px 6px",
            borderTop: `1px solid ${colors.border}`,
            marginTop: 6,
          }}>
            {isConnected ? (
              <button
                onClick={() => { disconnect(); setMenuOpen(false); }}
                style={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 12,
                  fontWeight: 600,
                  color: colors.green,
                  background: dark ? "rgba(46,160,67,0.1)" : "rgba(13,122,78,0.06)",
                  border: `1px solid ${dark ? 'rgba(46,160,67,0.25)' : 'rgba(13,122,78,0.2)'}`,
                  borderRadius: 10,
                  padding: "12px 16px",
                  cursor: "pointer",
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                }}
              >
                <span style={{
                  width: 7, height: 7, borderRadius: "50%",
                  background: colors.green,
                  boxShadow: `0 0 4px ${colors.green}`,
                }} />
                {account?.slice(0, 6)}...{account?.slice(-4)}
                <span style={{ fontSize: 10, color: colors.textSecondary, marginLeft: 4 }}>Disconnect</span>
              </button>
            ) : (
              <button
                onClick={() => { connect(); setMenuOpen(false); }}
                disabled={isConnecting}
                style={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#FFFFFF",
                  background: colors.green,
                  border: "none",
                  borderRadius: 10,
                  padding: "12px 16px",
                  cursor: isConnecting ? "default" : "pointer",
                  opacity: isConnecting ? 0.6 : 1,
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                }}
              >
                {isConnecting ? 'Connecting...' : '\uD83D\uDC5B Connect Wallet'}
              </button>
            )}
          </div>
          {renderMobileMenu()}
        </div>
      )}
    </>
  );
}
